import React from 'react';
import { css } from '@emotion/react';
import { FullTheme, useFullTheme } from '~/design';
import { Intent } from '~/design/types';
import { useResponsive } from '~/utils';
import { CloseOutlineIcon } from '@applyboard/ui-icons';
import { svgVerticalAlign } from '~/utils/styling';
import { IconWrapper } from '../IconWrapper';

interface CommonProps {
  /** The aria-activedescendant attribute identifies the currently active element
   * when focus is on a composite widget, combobox, textbox, group, or
   * application. */
  'aria-activedescendant'?: string;

  /** The aria-autocomplete of the input. */
  'aria-autocomplete'?: 'none' | 'inline' | 'list' | 'both';

  /** The aria-controls of the input. */
  'aria-controls'?: string;

  /** The aria-decsribedby of the input. */
  'aria-describedby'?: string;

  /** The aria-expanded of the input. */
  'aria-expanded'?: boolean | 'true' | 'false';

  /** The aria-label of the input. */
  'aria-label'?: string;

  /** The aria-labelledby of the input. */
  'aria-labelledby'?: string;

  /** No border when set to true. Used by MultiCombobox and elsewhere internally. */
  borders?: boolean;

  /** If true, the input cannot be interacted with. */
  disabled?: boolean;

  /** If type is search, hides the native "X" button from input. defaults to true if `onClear` is provided. */
  hideNativeSearchClear?: boolean;

  /** The ID of the input. */
  id?: string;

  /** The intent of the input. Maps to color. */
  intent?: Intent;

  /** Invalid input if true */
  invalid?: boolean;

  /** The node to put at the start of the input */
  leadAdornment?: React.ReactNode;

  /** If type='number', sets the maximum value for the input */
  max?: number;

  /** If type='number', sets the minimum value for the input */
  min?: number;

  /** When provided, shows a "x" icon to the right of the input. Returns the event object. */
  onClear?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;

  /** The placeholder of the Combobox. Placeholders are considered bad for accessibility, so it's *strongly encouraged* to avoid using this.
   * See this article for more details: https://www.smashingmagazine.com/2018/06/placeholder-attribute/ */
  placeholder?: string;

  /** Required in form if true */
  required?: boolean;

  /** The role of the input. */
  role?: 'combobox';

  /** The height of the input. (The width always expands to fit the parent element.) */
  size?: 'sm' | 'md' | 'lg';

  /** If type='number', sets the step interval between numbers */
  step?: number;

  /** The node to put at the end of the input */
  trailAdornment?: React.ReactNode;

  /** The value of the input control */
  value: string;
}

interface TextInputProps extends CommonProps {
  /** A callback function that is invoked when the focus changes for the input control */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;

  /** A callback function that is invoked when the values are entered in the input control */
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;

  /** A callback function that is invoked when the input control receives focus */
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;

  /** A callback function that is invoked when the input control receives a key down event */
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;

  /** What type will the input be */
  type?: 'text' | 'password' | 'search' | 'number' | 'time' | 'tel' | 'integer' | 'email';
}

interface TextareaProps extends CommonProps {
  /** A callback function that is invoked when the focus changes for the input control */
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;

  /** A callback function that is invoked when the values are entered in the input control */
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;

  /** A callback function that is invoked when the input control receives focus */
  onFocus?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;

  /** A callback function that is invoked when the input control receives a key down event */
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;

  /** The number of rows for the textarea */
  rows?: number;

  /** What type will the input be */
  type?: 'textarea';
}

export type InternalTextInputProps = TextInputProps | TextareaProps;

export const InternalTextInput = React.forwardRef<HTMLInputElement, InternalTextInputProps>(
  function InternalTextInput(
    {
      'aria-activedescendant': ariaActiveDescendant,
      'aria-autocomplete': ariaAutocomplete,
      'aria-controls': ariaControls,
      'aria-describedby': ariaDescribedBy,
      'aria-expanded': ariaExpanded,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      borders = true,
      disabled = false,
      hideNativeSearchClear: _hideNativeSearchClear = false,
      id,
      intent = 'secondary',
      invalid = false,
      leadAdornment,
      max,
      min,
      onBlur,
      onChange,
      onClear,
      onFocus,
      onKeyDown,
      placeholder,
      required = false,
      role,
      // @ts-expect-error TS discriminated union doesn't work as expected
      rows,
      size = 'md',
      step,
      trailAdornment,
      type = 'text',
      value = '',
    },
    ref,
  ) {
    const theme = useFullTheme();
    const adjustedIntent = invalid ? 'negative' : intent;
    const responsive = useResponsive();
    const colorUtil = createInputColorUtil(theme);

    const paddingRight = 44;
    const itemsOnRight = [onClear, trailAdornment].filter(item => !!item);

    const hideNativeSearchClear = onClear ? true : _hideNativeSearchClear;

    const sizeStyle = {
      sm: css({
        height: type === 'textarea' ? `calc(${rows} * ${theme.spacing[10]})` : theme.spacing[10],
        minHeight: type === 'textarea' ? theme.spacing[10] : undefined,
        ...responsive.css(theme.typography.bodyS),
      }),
      md: css({
        height: type === 'textarea' ? `calc(${rows} * ${theme.spacing[12]})` : theme.spacing[12],
        minHeight: type === 'textarea' ? theme.spacing[12] : undefined,
        ...responsive.css(theme.typography.bodyM),
        ...responsive.css({
          fontSize: {
            ...(typeof theme.typography.bodyM.fontSize === 'string'
              ? { sm: theme.typography.bodyM.fontSize }
              : theme.typography.bodyM.fontSize),
            // On iOS devices, if the font size is smaller than 16px, the device will zoom in.
            // To prevent this, we'll make it 16px for the text input's bodyM font instead of
            // changing the typography across the product for XS screens.
            xs: '1rem',
          },
        }),
      }),
      lg: css({
        height: type === 'textarea' ? `calc(${rows} * ${theme.spacing[14]})` : theme.spacing[14],
        minHeight: type === 'textarea' ? theme.spacing[14] : undefined,
        ...responsive.css(theme.typography.bodyL),
      }),
    }[size];

    const inputStyle = css({
      backgroundColor: {
        magic: theme.colors.bgMagicHighlight,
        negative: theme.colors.bgNegativeHighlight,
        positive: theme.colors.bgPositiveHighlight,
        primary: theme.colors.bgPrimaryHighlight,
        secondary: theme.colors.bgRegular,
        warning: theme.colors.bgWarningHighlight,
      }[intent],
      borderColor: colorUtil('border', adjustedIntent),
      borderRadius: theme.borderRadiuses.control,
      borderWidth: theme.spacing['1px'],
      color: {
        magic: theme.colors.textMagicStark,
        negative: theme.colors.textNegativeStark,
        positive: theme.colors.textPositiveStark,
        primary: theme.colors.textPrimaryStark,
        secondary: theme.colors.textSecondaryStark,
        warning: theme.colors.textWarningStark,
      }[adjustedIntent],
      paddingBlock: type === 'textarea' ? theme.spacing[2] : theme.spacing[0],
      paddingInline: theme.spacing[4],
      width: '100%',
      // removes the native X when type === search and hideNativeSearch is true
      '&::-webkit-search-cancel-button, &::-webkit-search-decoration': type === 'search' &&
        hideNativeSearchClear && {
          WebkitAppearance: 'none',
          appearance: 'none',
        },
    });

    const borderStyle = borders
      ? {
          borderStyle: 'solid',
          '&:enabled:hover': {
            borderColor: colorUtil('borderHover', adjustedIntent),
            backgroundColor: {
              magic: theme.colors.bgMagicHighlightHover,
              negative: theme.colors.bgNegativeHighlightHover,
              positive: theme.colors.bgPositiveHighlightHover,
              primary: theme.colors.bgPrimaryHighlightHover,
              secondary: theme.colors.bgRegularHover,
              warning: theme.colors.bgWarningHighlightHover,
            }[intent],
          },
          '&:enabled:focus-visible': {
            outlineWidth: theme.spacing['2px'],
            outlineStyle: 'solid',
            outlineColor: colorUtil('outline', adjustedIntent),
            borderColor: 'transparent',
          },
          '&:disabled': {
            borderColor: theme.colors.borderInactive,
            backgroundColor: theme.colors.bgInactive,
            color: theme.colors.textInactive,
            cursor: 'not-allowed',
          },
        }
      : css({
          borderStyle: 'none',
          outline: 'unset',
        });

    const InputTag = type === 'textarea' ? 'textarea' : 'input';
    const isNumeric = ['number', 'integer'].includes(type);

    return (
      <div style={{ position: 'relative', width: '100%' }}>
        {leadAdornment && (
          <div
            css={[
              {
                alignItems: 'center',
                display: 'flex',
                height: '100%',
                left: 0,
                paddingLeft: theme.spacing[4],
                paddingRight: theme.spacing[4],
                position: 'absolute',
                top: 0,
              },
              svgVerticalAlign,
            ]}
          >
            {leadAdornment}
          </div>
        )}
        <InputTag
          aria-activedescendant={ariaActiveDescendant}
          aria-autocomplete={ariaAutocomplete}
          aria-controls={ariaControls}
          aria-describedby={ariaDescribedBy}
          aria-expanded={ariaExpanded}
          aria-invalid={invalid}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          css={[
            sizeStyle,
            inputStyle,
            borderStyle,
            css({
              paddingRight: itemsOnRight.length
                ? `${itemsOnRight.length * paddingRight}px`
                : theme.spacing[4],
            }),
            leadAdornment &&
              css({
                paddingLeft: '52px',
              }),
            type === 'textarea' && {
              resize: 'vertical',
            },
            type === 'textarea' &&
              disabled && {
                resize: 'none',
              },
          ]}
          disabled={disabled}
          id={id}
          inputMode={type === 'integer' ? 'numeric' : isNumeric ? 'decimal' : undefined}
          max={isNumeric && max ? max : undefined}
          min={isNumeric && typeof min === 'number' ? min : undefined}
          // @ts-expect-error TS discriminated union doesn't work as expected
          onBlur={onBlur}
          // @ts-expect-error TS discriminated union doesn't work as expected
          onChange={onChange}
          // @ts-expect-error TS discriminated union doesn't work as expected
          onFocus={onFocus}
          // @ts-expect-error TS discriminated union doesn't work as expected
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          // @ts-expect-error Not sure how to type ref such that it works for both input, textarea
          ref={ref}
          role={role}
          rows={type === 'textarea' ? rows : undefined}
          required={required}
          step={isNumeric && step ? step : undefined}
          type={isNumeric ? 'number' : type}
          value={value}
        />
        {(trailAdornment || onClear) && (
          <div
            css={{
              alignItems: 'center',
              display: 'flex',
              height: '100%',
              paddingLeft: theme.spacing[2],
              paddingRight: theme.spacing[2],
              position: 'absolute',
              right: 0,
              top: 0,
            }}
          >
            {onClear && (
              <button
                css={{
                  backgroundColor: 'transparent',
                  borderStyle: 'none',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
                aria-label="Clear"
                onClick={e => onClear(e)}
                type="button"
              >
                <IconWrapper
                  contrast="high"
                  disabled={disabled}
                  intent={adjustedIntent}
                  selector="button"
                >
                  <CloseOutlineIcon />
                </IconWrapper>
              </button>
            )}
            {trailAdornment}
          </div>
        )}
      </div>
    );
  },
);

export function createInputColorUtil({ colors }: FullTheme) {
  const inputColors = {
    outline: {
      magic: colors.actionMagic,
      negative: colors.actionNegative,
      positive: colors.actionPositive,
      primary: colors.actionPrimary,
      secondary: colors.actionPrimary, // actionPrimary used intentionally
      warning: colors.actionWarning,
    },
    border: {
      magic: colors.borderMagic,
      negative: colors.borderNegative,
      positive: colors.borderPositive,
      primary: colors.borderPrimary,
      secondary: colors.borderSecondary,
      warning: colors.borderWarning,
    },
    borderHover: {
      magic: colors.borderMagicHover,
      negative: colors.borderNegativeHover,
      positive: colors.borderPositiveHover,
      primary: colors.borderPrimaryHover,
      secondary: colors.borderSecondaryHover,
      warning: colors.borderWarningHover,
    },
  };

  return (purpose: keyof typeof inputColors, intent: Intent) => {
    return inputColors[purpose][intent];
  };
}
